import { useTranslation } from 'react-i18next';
import { Button, RewardBadge, TokenIcon, toast } from '@shared/ui';
import { useDailyTaskQuery, useStartDailyTaskMutation } from '../model/queries';
import { PixelCardsIcon } from './PixelCardsIcon';

interface DailyTaskRowProps {
  /** Открыть знакомство со словами задания. */
  onOpen: () => void;
}

/**
 * «Задание дня» — строка плашки «На сегодня» на главной (решение владельца 2026-10-23: «две огромные плашки слишком много
 * для ежедневок» — задание и миссия в одной плашке, с пиксель-артом игры). 5 новых слов из готовых модулей,
 * знакомство, тренировка со случайными упражнениями и несгораемые жетоны. Пока задание грузится или не загрузилось —
 * строки нет. Кнопка с обводкой: оранжевая на главной одна — «Тренироваться» (владелец 2026-10-22).
 */
export function DailyTaskRow({ onOpen }: DailyTaskRowProps) {
  const { t } = useTranslation();
  const query = useDailyTaskQuery();
  const start = useStartDailyTaskMutation();
  const task = query.data;
  if (!task) return null;

  const take = () => {
    if (start.isPending) return;
    start.mutate(undefined, {
      onSuccess: (fresh) => {
        if (fresh.status === 'ready') onOpen();
      },
      onError: () => toast({ tone: 'error', message: t('dailyTask.startError') }),
    });
  };

  const done = task.status === 'done';
  const empty = task.status === 'empty';
  const title = done
    ? t('dailyTask.doneTitle')
    : empty
      ? t('dailyTask.emptyTitle')
      : t('dailyTask.title', { count: task.wordsCount });

  return (
    <div className="dl-row flex items-center gap-3">
      <span aria-hidden className="dl-art lx-px-sm grid h-12 w-12 flex-none place-items-center bg-[#22263f]">
        <PixelCardsIcon className="h-8 w-8" />
      </span>
      <div className="dl-text min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-[12px] font-semibold uppercase tracking-[0.12em] text-muted">
          {t('dailyTask.kicker')}
          {!done && !empty && (
            <span className="inline-flex items-center gap-1 rounded-pill bg-accent/15 px-1.5 py-0.5 text-[11px] font-semibold normal-case tracking-normal text-accent">
              <TokenIcon size={12} />
              {t('dailyTask.reward', { count: task.rewardTokens })}
            </span>
          )}
        </div>
        <h3 className="mt-0.5 line-clamp-2 break-words font-serif text-[17px] font-medium leading-[22px] text-fg">{title}</h3>
      </div>
      <div className="dl-action flex-none">
        {task.status === 'available' ? (
          <Button variant="outline" size="sm" onClick={take} disabled={start.isPending}>
            {start.isPending ? t('dashboard.home.dailies.taking') : t('dashboard.home.dailies.take')}
          </Button>
        ) : task.status === 'ready' ? (
          <Button variant="outline" size="sm" onClick={onOpen}>
            {t('dashboard.home.dailies.learn')}
          </Button>
        ) : done && task.reward > 0 ? (
          <RewardBadge size="sm" amount={task.reward} caption={t('rewards.earned', { count: task.reward })} />
        ) : null}
      </div>
    </div>
  );
}
